import React, { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";

import AddEvent from "./AddEvent";
import api from "./api";

import {
    AppBar,
    Toolbar,
    Box,
    Container,
    Typography,
    Button,
    TextField,
    Paper,
    Card,
    CardMedia,
    CardContent,
    CardActions,
    Grid,
    Stack,
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    Divider,
    InputAdornment
} from "@mui/material";


const Dashboard = () => {

    const [search, setSearch] = useState("");
    const [events, setEvents] = useState([]);
    const [showModal, setShowModal] = useState(false);

    const navigate = useNavigate();

    // Get token from localStorage
    const token = JSON.parse(localStorage.getItem("token") || "null");

    // Decode JWT token
    function parseJwt(token) {
        try {
            const base64Payload = token
                .split(".")[1]
                .replace(/-/g, "+")
                .replace(/_/g, "/");

            return JSON.parse(atob(base64Payload));
        } catch (e) {
            return null;
        }
    }

    const payload = parseJwt(token);

    // Handle role whether it is a string or an array
    const roles = Array.isArray(payload?.role)
        ? payload.role
        : payload?.role
            ? [payload.role]
            : [];

    const isAdmin = roles.includes("ROLE_ADMIN");

    const isLoggedIn = Boolean(token);


    // Fetch all events
    const getEvents = async () => {
        try {
            const res = await api.get("/events/getEvents");
            setEvents(res.data);
        } catch (e) {
            console.error("Error fetching events:", e);
        }
    };


    useEffect(() => {
        getEvents();
    }, []);


    // Delete event
    const deleteEvent = async (eventId) => {

        if (!window.confirm("Are you sure you want to delete this event?")) {
            return;
        }

        try {
            await api.delete(`/events/delete/${eventId}`);

            alert("Event deleted successfully!");

            getEvents();

        } catch (error) {
            console.error("Error deleting event:", error);

            alert("Failed to delete the event. Try again.");
        }
    };


    // Logout
    const handleLogout = () => {

        localStorage.removeItem("token");
        localStorage.removeItem("role");

        navigate("/");
        window.location.reload();
    };


    // Smooth scrolling
    const scrollToSection = (e, sectionId) => {

        e.preventDefault();

        const section = document.getElementById(sectionId);

        if (section) {
            const offset = 85;

            const sectionPosition =
                section.getBoundingClientRect().top +
                window.scrollY -
                offset;

            window.scrollTo({
                top: sectionPosition,
                behavior: "smooth"
            });
        }
    };


    // Filter events
    const filteredEvents = events.filter((event) =>
        event.name?.toLowerCase().includes(search.toLowerCase())
    );


    return (

        <Box sx={{ backgroundColor: "#fafafa", minHeight: "100vh" }}>

            {/* ================= HEADER ================= */}

            <AppBar
                position="sticky"
                elevation={0}
                sx={{
                    backgroundColor: "rgba(20,20,20,0.97)",
                    backdropFilter: "blur(10px)"
                }}
            >
                <Toolbar
                    sx={{
                        maxWidth: "1400px",
                        width: "100%",
                        mx: "auto",
                        py: 1,
                        display: "flex",
                        justifyContent: "space-between",
                        flexWrap: "wrap",
                        gap: 2
                    }}
                >

                    {/* Logo */}
                    <Typography
                        variant="h5"
                        sx={{
                            fontWeight: 800,
                            letterSpacing: 1,
                            color: "#fff"
                        }}
                    >
                        Jenny
                        <Box
                            component="span"
                            sx={{ color: "#d4af75" }}
                        >
                            Photography.
                        </Box>
                    </Typography>


                    {/* Navigation */}
                    <Stack
                        direction="row"
                        spacing={{ xs: 1, sm: 2 }}
                        alignItems="center"
                        flexWrap="wrap"
                    >

                        {[
                            ["Home", "home"],
                            ["About", "about"],
                            ["Events", "events"],
                            ["Contact", "contact"]
                        ].map(([label, id]) => (

                            <Button
                                key={id}
                                color="inherit"
                                onClick={(e) => scrollToSection(e, id)}
                                sx={{
                                    color: "#eee",
                                    textTransform: "none",
                                    fontSize: 14,
                                    "&:hover": {
                                        color: "#d4af75"
                                    }
                                }}
                            >
                                {label}
                            </Button>

                        ))}


                        {!isLoggedIn ? (

                            <>
                                <Button
                                    component={NavLink}
                                    to="/login"
                                    variant="outlined"
                                    sx={{
                                        color: "#fff",
                                        borderColor: "#d4af75",
                                        textTransform: "none",
                                        "&:hover": {
                                            backgroundColor: "#d4af75",
                                            color: "#111"
                                        }
                                    }}
                                >
                                    Login
                                </Button>

                                <Button
                                    component={NavLink}
                                    to="/signup"
                                    variant="contained"
                                    sx={{
                                        backgroundColor: "#d4af75",
                                        color: "#111",
                                        textTransform: "none",
                                        "&:hover": {
                                            backgroundColor: "#bd965e"
                                        }
                                    }}
                                >
                                    Sign Up
                                </Button>
                            </>

                        ) : (

                            <Button
                                variant="outlined"
                                onClick={handleLogout}
                                sx={{
                                    color: "#fff",
                                    borderColor: "#d4af75",
                                    textTransform: "none"
                                }}
                            >
                                Logout
                            </Button>

                        )}

                    </Stack>

                </Toolbar>
            </AppBar>


            {/* ================= HERO SECTION ================= */}

            <Box
                id="home"
                sx={{
                    minHeight: { xs: "550px", md: "650px" },
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    textAlign: "center",
                    color: "#fff",
                    background: `
                        linear-gradient(
                            rgba(0,0,0,0.65),
                            rgba(0,0,0,0.65)
                        ),
                        linear-gradient(135deg, #272727, #65513e, #171717)
                    `,
                    px: 2
                }}
            >

                <Container maxWidth="md">

                    <Typography
                        variant="overline"
                        sx={{
                            color: "#d4af75",
                            letterSpacing: 5,
                            fontSize: 14
                        }}
                    >
                        JENNY PHOTOGRAPHY
                    </Typography>

                    <Typography
                        variant="h2"
                        sx={{
                            fontWeight: 800,
                            mt: 2,
                            mb: 3,
                            fontSize: {
                                xs: "2.5rem",
                                md: "4.5rem"
                            }
                        }}
                    >
                        Capture Moments.
                        <br />
                        <Box
                            component="span"
                            sx={{ color: "#d4af75" }}
                        >
                            Create Memories.
                        </Box>
                    </Typography>

                    <Typography
                        variant="h6"
                        sx={{
                            color: "#ddd",
                            maxWidth: 650,
                            mx: "auto",
                            mb: 4,
                            fontWeight: 400,
                            lineHeight: 1.8
                        }}
                    >
                        Professional photography services.
                        Preserving your special moments with creativity,
                        care, and style.
                    </Typography>

                    <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={2}
                        justifyContent="center"
                    >

                        <Button
                            variant="contained"
                            onClick={(e) => scrollToSection(e, "events")}
                            sx={{
                                backgroundColor: "#d4af75",
                                color: "#111",
                                px: 4,
                                py: 1.5,
                                fontWeight: 700,
                                "&:hover": {
                                    backgroundColor: "#bd965e"
                                }
                            }}
                        >
                            Explore Events
                        </Button>

                        <Button
                            variant="outlined"
                            onClick={(e) => scrollToSection(e, "contact")}
                            sx={{
                                color: "#fff",
                                borderColor: "#fff",
                                px: 4,
                                py: 1.5
                            }}
                        >
                            Contact Us
                        </Button>

                    </Stack>


                    {/* Admin Add Event Button */}
                    {isAdmin && (
                        <Button
                            variant="contained"
                            onClick={() => setShowModal(true)}
                            sx={{
                                mt: 4,
                                backgroundColor: "#fff",
                                color: "#111",
                                "&:hover": {
                                    backgroundColor: "#eee"
                                }
                            }}
                        >
                            + Add Event
                        </Button>
                    )}

                </Container>

            </Box>


            {/* ================= ABOUT SECTION ================= */}

            <Box
                id="about"
                sx={{
                    py: { xs: 7, md: 11 },
                    backgroundColor: "#fff"
                }}
            >

                <Container maxWidth="md">

                    <Typography
                        variant="overline"
                        textAlign="center"
                        display="block"
                        sx={{
                            color: "#b18a52",
                            letterSpacing: 4
                        }}
                    >
                        WHO WE ARE
                    </Typography>

                    <Typography
                        variant="h3"
                        textAlign="center"
                        fontWeight={800}
                        sx={{ mb: 4 }}
                    >
                        About Jenny Photography
                    </Typography>

                    <Paper
                        elevation={0}
                        sx={{
                            p: { xs: 3, md: 5 },
                            backgroundColor: "#f8f6f2",
                            borderLeft: "5px solid #d4af75",
                            borderRadius: 2
                        }}
                    >

                        <Typography
                            variant="body1"
                            textAlign="center"
                            sx={{
                                color: "#555",
                                lineHeight: 2,
                                fontSize: 17
                            }}
                        >
                            Jenny Photography is a team of professional
                            photographers dedicated to capturing life's
                            most precious moments.

                            We specialize in events, portraits, weddings,
                            and artistic photography. Our mission is to
                            combine creativity with technical expertise
                            to deliver timeless memories.
                        </Typography>

                    </Paper>

                </Container>

            </Box>


            {/* ================= EVENTS SECTION ================= */}

            <Box
                id="events"
                sx={{
                    py: { xs: 7, md: 10 },
                    backgroundColor: "#f5f5f5"
                }}
            >

                <Container maxWidth="xl">

                    <Typography
                        variant="overline"
                        textAlign="center"
                        display="block"
                        sx={{
                            color: "#b18a52",
                            letterSpacing: 4
                        }}
                    >
                        OUR COLLECTION
                    </Typography>

                    <Typography
                        variant="h3"
                        textAlign="center"
                        fontWeight={800}
                        sx={{ mb: 2 }}
                    >
                        Our Events
                    </Typography>

                    <Typography
                        textAlign="center"
                        color="text.secondary"
                        sx={{
                            maxWidth: 750,
                            mx: "auto",
                            mb: 5,
                            lineHeight: 1.8
                        }}
                    >
                        Step into a world of captured memories!
                        Explore stunning photographs and captivating
                        videos that tell unique stories. Discover
                        the magic through our lens.
                    </Typography>


                    {/* Search Field */}
                    <Box
                        sx={{
                            maxWidth: 450,
                            mx: "auto",
                            mb: 6
                        }}
                    >

                        <TextField
                            fullWidth
                            placeholder="Search events..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            size="medium"
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Typography>⌕</Typography>
                                    </InputAdornment>
                                )
                            }}
                            sx={{
                                backgroundColor: "#fff",
                                borderRadius: 2
                            }}
                        />

                    </Box>


                    {/* Event Cards */}
                    <Grid container spacing={3}>

                        {filteredEvents.length > 0 ? (

                            filteredEvents.map((event) => (

                                <Grid
                                    item
                                    xs={12}
                                    sm={6}
                                    md={4}
                                    lg={3}
                                    key={event.id}
                                >

                                    <Card
                                        onClick={() =>
                                            navigate(`/images/${event.id}`)
                                        }
                                        sx={{
                                            height: 350,
                                            position: "relative",
                                            cursor: "pointer",
                                            overflow: "hidden",
                                            borderRadius: 3,
                                            boxShadow: 2,
                                            "&:hover img": {
                                                transform: "scale(1.08)"
                                            },
                                            "&:hover .event-overlay": {
                                                backgroundColor:
                                                    "rgba(0,0,0,0.75)"
                                            }
                                        }}
                                    >

                                        <CardMedia
                                            component="img"
                                            image={
                                                event.coverImageUrl ||
                                                "https://via.placeholder.com/300"
                                            }
                                            alt={event.name}
                                            sx={{
                                                height: "100%",
                                                transition: "transform 0.5s"
                                            }}
                                        />

                                        <Box
                                            className="event-overlay"
                                            sx={{
                                                position: "absolute",
                                                inset: 0,
                                                display: "flex",
                                                flexDirection: "column",
                                                justifyContent: "flex-end",
                                                alignItems: "center",
                                                p: 3,
                                                color: "#fff",
                                                backgroundColor:
                                                    "rgba(0,0,0,0.4)",
                                                transition: "background-color 0.3s"
                                            }}
                                        >

                                            <Typography
                                                variant="h5"
                                                fontWeight="bold"
                                                textAlign="center"
                                            >
                                                {event.name}
                                            </Typography>

                                            {isAdmin && (
                                                <Button
                                                    variant="contained"
                                                    color="error"
                                                    size="small"
                                                    sx={{ mt: 2 }}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        deleteEvent(event.id);
                                                    }}
                                                >
                                                    Delete Event
                                                </Button>
                                            )}

                                        </Box>

                                    </Card>

                                </Grid>

                            ))

                        ) : (

                            <Grid item xs={12}>
                                <Paper
                                    elevation={0}
                                    sx={{
                                        p: 5,
                                        textAlign: "center",
                                        backgroundColor: "#fff"
                                    }}
                                >
                                    <Typography
                                        variant="h6"
                                        color="text.secondary"
                                    >
                                        No events found.
                                    </Typography>
                                </Paper>
                            </Grid>

                        )}

                    </Grid>

                </Container>

            </Box>


            {/* ================= CONTACT SECTION ================= */}

            <Box
                id="contact"
                sx={{
                    py: 9,
                    backgroundColor: "#171717",
                    color: "#fff"
                }}
            >

                <Container maxWidth="md">

                    <Typography
                        variant="overline"
                        textAlign="center"
                        display="block"
                        sx={{
                            color: "#d4af75",
                            letterSpacing: 4
                        }}
                    >
                        GET IN TOUCH
                    </Typography>

                    <Typography
                        variant="h3"
                        textAlign="center"
                        fontWeight={800}
                        sx={{ mb: 5 }}
                    >
                        Contact Us
                    </Typography>

                    <Grid container spacing={3}>

                        {[
                            {
                                title: "Email",
                                value: "info@jennyphotography.com"
                            },
                            {
                                title: "Phone",
                                value: "+91 9182236453"
                            },
                            {
                                title: "Location",
                                value: "C-3, Chilkode"
                            }
                        ].map((item) => (

                            <Grid item xs={12} sm={4} key={item.title}>

                                <Paper
                                    elevation={0}
                                    sx={{
                                        p: 3,
                                        textAlign: "center",
                                        height: "100%",
                                        backgroundColor: "#252525",
                                        color: "#fff",
                                        borderRadius: 2
                                    }}
                                >

                                    <Typography
                                        variant="h6"
                                        sx={{
                                            color: "#d4af75",
                                            mb: 1
                                        }}
                                    >
                                        {item.title}
                                    </Typography>

                                    <Typography
                                        sx={{
                                            color: "#ddd",
                                            overflowWrap: "anywhere"
                                        }}
                                    >
                                        {item.value}
                                    </Typography>

                                </Paper>

                            </Grid>

                        ))}

                    </Grid>


                    {/* Social Media Links */}
                    <Stack
                        direction="row"
                        spacing={2}
                        justifyContent="center"
                        flexWrap="wrap"
                        sx={{ mt: 5 }}
                    >

                        <Button
                            component="a"
                            href="https://www.instagram.com/ramprasadarvapalli?utm_source=qr&igsh=YzZ3amxzbGoxMTI5"
                            target="_blank"
                            rel="noopener noreferrer"
                            variant="contained"
                            sx={{
                                backgroundColor: "#c13584",
                                "&:hover": {
                                    backgroundColor: "#a82b70"
                                }
                            }}
                        >
                            Instagram
                        </Button>

                        <Button
                            component="a"
                            href="https://wa.me/919398217894?text=Hello%20I%20want%20to%20book%20a%20photography%20session"
                            target="_blank"
                            rel="noopener noreferrer"
                            variant="contained"
                            sx={{
                                backgroundColor: "#25D366",
                                color: "#fff",
                                "&:hover": {
                                    backgroundColor: "#1da851"
                                }
                            }}
                        >
                            WhatsApp
                        </Button>

                    </Stack>

                </Container>

            </Box>


            {/* ================= FOOTER ================= */}

            <Box
                sx={{
                    py: 3,
                    textAlign: "center",
                    backgroundColor: "#101010",
                    color: "#aaa"
                }}
            >

                <Divider
                    sx={{
                        borderColor: "#333",
                        mb: 3
                    }}
                />

                <Typography variant="body2">
                    © 2026 Jenny Photography. All rights reserved.
                </Typography>

            </Box>


            {/* ================= ADD EVENT DIALOG ================= */}

            <Dialog
                open={showModal && isAdmin}
                onClose={() => setShowModal(false)}
                fullWidth
                maxWidth="sm"
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        overflow: "hidden"
                    }
                }}
            >

                <DialogTitle
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        fontWeight: "bold"
                    }}
                >
                    Add New Event

                    <IconButton
                        onClick={() => setShowModal(false)}
                        aria-label="Close dialog"
                    >
                        ×
                    </IconButton>

                </DialogTitle>

                <DialogContent dividers>

                    <AddEvent
                        onClose={() => setShowModal(false)}
                        refreshEvents={getEvents}
                    />

                </DialogContent>

            </Dialog>

        </Box>
    );
};

export default Dashboard;
